/**
 * The Kalendarz tab: the toolbar over the grid, and the right column beside it.
 *
 * ── It loads without moving ──────────────────────────────────────────────────────────────────
 * Both boxes have their size from the first frame: the grid takes whatever the row leaves after the
 * 404px column, and FullCalendar scrolls its body inside it. The wait is ONE gate over the grid's
 * two reads, and both boxes fork on it — `waiting ? bars : failed ? error : content` — so the grid
 * and the day's list arrive together, on the same hand-over. The grid's own chunk is lazy; its
 * Suspense fallback is the same still bars, so a slow chunk and a slow read look alike.
 *
 * The one-time Google card (`GoogleCalendarPrompt`) stands over the grid inside its box. Its read
 * is in the same gate and it is decided on the hand-over, so it arrives with the grid or not at
 * all, and it stays across later waits of the grid; only „Nie teraz" or a new answer take it away.
 *
 * ── The grid changing what it shows ──────────────────────────────────────────────────────────
 * A page, "Dziś" or the week ↔ month switch swaps the grid in ONE quick step from the motion
 * dictionary (`calendarView` / `calendarForward` / `calendarBack`: 150ms, ease-out, from 40%
 * opacity with a 6px drift for a page), played by `useMotionOnKeyChange` on a wrapper that stands
 * across the swap. The outgoing view is not kept: FullCalendar remounts on the key, so a kept
 * snapshot would be a second calendar under the first. The new one is simply drawn at once and
 * its ink settles — never blank, never waiting. The neighbouring pages are read ahead
 * (`useBookingCalendar`), so a page answers from the cache; one that has not answered yet is the
 * skeleton policy's, and its arrival is `.reveal`, never this step. Reduced motion: the step's
 * start is its end, at zero duration — an instant swap.
 *
 * ── The day's plan ───────────────────────────────────────────────────────────────────────────
 * Hidden by default, out on the toolbar's toggle (remembered per viewer), and out for as long as a
 * session is open in it — closing the session returns the column to the viewer's choice.
 *
 * ── Creating and moving ──────────────────────────────────────────────────────────────────────
 * `useSessionWrites` owns the writes. A new session is composed in the column (it takes the
 * column's place ahead of the day and an open session, and brings the column out while it is
 * open); "+" in the screen's header row starts one at the next quarter hour, a span dragged on the
 * week or a day clicked on the month starts one there. "Powtarzaj…" hands it to the series window.
 * A move made on the grid is drawn where it was dropped until the calendar is read again, and a
 * refusal is said in one quiet line over the grid's foot — which never moves the grid.
 *
 * ── Keyboard ─────────────────────────────────────────────────────────────────────────────────
 * The grid is one tab stop walked by the arrows (`SessionGrid`); a skip button in front of it goes
 * straight to the day's plan. Enter or Space on a block opens the session in the column, which
 * takes focus; Escape closes it and hands focus back to where it came from — the block, or the
 * agenda row when that row is still on screen.
 */

import { motion } from 'motion/react';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import {
  screenMotion,
  useMotionOnKeyChange,
  useScreenMotionPreference,
} from '~/shared/design-system/motion';
import { EmptyState } from '~/shared/ui/EmptyState';
import { CalendarMarkIcon } from '~/shared/ui/EmptyStateIcons';
import ember from '~/shared/ui/emberButton.module.css';
import { CloseGlyph } from '~/shared/ui/FormGlyphs';
import ghost from '~/shared/ui/ghostButton.module.css';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import { ScreenHeaderPlus } from '~/shared/ui/ScreenHeader';
import header from '~/shared/ui/screenHeader.module.css';
import skeleton from '~/shared/ui/skeleton.module.css';
import stateAction from '~/shared/ui/stateAction.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useBookingCalendar } from '../lib/useBookingCalendar';
import { useGoogleCalendarPrompt } from '../lib/useGoogleCalendarPrompt';
import { useSessionWrites } from '../lib/useSessionWrites';
import { createdNotYetRead } from '../model/sessionDraft';
import { withPendingMoves } from '../model/sessionMove';
import { CalendarToolbar } from './CalendarToolbar';
import { DayAgendaPanel } from './DayAgendaPanel';
import { GoogleCalendarPrompt } from './GoogleCalendarPrompt';
import { NewSessionPanel } from './NewSessionPanel';
import { SeriesDialog } from './SeriesDialog';
import { SessionDetailPanel } from './SessionDetailPanel';
import styles from './CalendarTab.module.css';

const SessionGrid = lazy(() =>
  import('./SessionGrid').then((module) => ({ default: module.SessionGrid })),
);

const SKELETON_COLUMNS = 7;
const SKELETON_ROWS = 4;

function GridBars({ still }: { readonly still?: boolean }) {
  const bar = `${String(skeleton.bar)} ${still === true ? String(skeleton.still) : ''}`;
  return (
    <div className={styles.gridBars} aria-hidden="true">
      {Array.from({ length: SKELETON_COLUMNS }, (_, column) => (
        <div key={column} className={styles.gridBarColumn}>
          <span className={`${bar} ${String(styles.gridBarHead)}`} />
          <span className={`${bar} ${String(styles.gridBarBody)}`} />
        </div>
      ))}
    </div>
  );
}

function AgendaBars() {
  return (
    <div className={styles.agendaBars} aria-hidden="true">
      <span className={`${String(skeleton.bar)} ${String(styles.agendaBarTitle)}`} />
      {Array.from({ length: SKELETON_ROWS }, (_, row) => (
        <span key={row} className={`${String(skeleton.bar)} ${String(styles.agendaBarRow)}`} />
      ))}
    </div>
  );
}

interface CalendarTabProps {
  /** The screen header's slot for the tab's own action — "+", the header's one accent. */
  readonly headerActions: HTMLElement | null;
}

export function CalendarTab({ headerActions }: CalendarTabProps) {
  const { t } = useTranslation();
  const state = useBookingCalendar();
  const writes = useSessionWrites(state);
  const { gate, content } = state;
  const prompt = useGoogleCalendarPrompt(gate);
  // The open session as the column shows it: where a move not yet read back put it.
  const openSession =
    state.openSession === null
      ? null
      : (withPendingMoves([{ session: state.openSession, live: false }], writes.pendingMoves, state.zone)[0]
          ?.session ?? state.openSession);
  const composing = writes.composing;
  const columnShown = state.columnShown || composing !== null;
  const opener = useRef<HTMLElement | null>(null);
  const composeOpener = useRef<HTMLElement | null>(null);
  const gridBox = useRef<HTMLElement>(null);
  const side = useRef<HTMLElement>(null);
  const [refocusStep, setRefocusStep] = useState<-1 | 1 | null>(null);
  const reducedMotion = useScreenMotionPreference();
  const gridShown = !gate.waiting && content?.kind === 'ready';
  const swap = useMotionOnKeyChange(
    gridShown ? `${state.view}:${state.range.first}` : null,
    screenMotion(state.gridMove, { reducedMotion }),
  );

  const openSessionFrom = (id: number) => {
    // A pointer press on a draggable block does not focus it (FullCalendar's drag takes the
    // mousedown), so the page itself can be what is active; the way back is then the block, by id.
    const active = document.activeElement;
    opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
    state.openSessionById(id);
  };

  /**
   * Remember where composing started, so closing the form hands focus back there. A gesture on
   * the grid focuses nothing (FullCalendar takes the mousedown), so whatever is active then is not
   * what opened the form: the way back from the grid is the grid's own stop.
   */
  const composeFrom = (start: () => void, onGrid: boolean) => {
    const active = document.activeElement;
    composeOpener.current =
      !onGrid && active instanceof HTMLElement && active !== document.body ? active : null;
    start();
  };

  /** The grid's own tab stop. */
  const focusGridStop = () => {
    const stop =
      gridBox.current?.querySelector<HTMLElement>('button[data-session-id][tabindex="0"]') ??
      gridBox.current?.querySelector<HTMLElement>('button');
    stop?.focus();
  };

  /**
   * "Nowa sesja" closed without a session: focus goes back to what opened it — "+", or, for a span
   * dragged out on the grid (which focuses nothing), the grid's own tab stop.
   */
  const closeDraft = () => {
    // A session is being sent: the form stays until the answer lands.
    if (writes.draftLocked) return;
    flushSync(() => {
      writes.closeDraft();
    });
    const back = composeOpener.current;
    composeOpener.current = null;
    if (back?.isConnected === true) {
      back.focus();
      return;
    }
    focusGridStop();
  };

  const closeSession = () => {
    const id = openSession?.id;
    state.closeSession();
    const back = opener.current;
    opener.current = null;
    if (back?.isConnected === true) {
      back.focus();
      return;
    }
    if (id === undefined) return;
    gridBox.current?.querySelector<HTMLElement>(`button[data-session-id="${String(id)}"]`)?.focus();
  };

  // Escape returns the column to the day while a session is open or one is being composed,
  // wherever focus is on the screen. The series window and the panel's confirm handle their own
  // Escape first: Base UI dismisses on `document` and marks the event handled, so this listens on
  // `window`, which the event reaches after — on `document` it could run first and close the
  // session under the confirm the trainer only meant to back out of.
  //
  // The month's "+N więcej" popover is FullCalendar's own: it closes on a `document` Escape and
  // marks nothing, and it is gone from the DOM by the time the event reaches `window`. So whether
  // it was open is read on the way DOWN (capture), and that Escape is left to it alone. It is found
  // by FullCalendar v7's own markup — a `role="dialog"` carrying the day's `data-date` — because
  // its class names are generated.
  useEffect(() => {
    if (openSession === null && composing === null) return;
    let popoverEscape: KeyboardEvent | null = null;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && document.querySelector('[role="dialog"][data-date]') !== null) {
        popoverEscape = event;
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (
        event.key !== 'Escape' ||
        event.defaultPrevented ||
        event === popoverEscape ||
        writes.seriesSeed !== null
      ) {
        return;
      }
      event.preventDefault();
      if (composing !== null) closeDraft();
      else closeSession();
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true });
      window.removeEventListener('keydown', onKey);
    };
  });

  return (
    <div className={styles.tab}>
      {headerActions === null
        ? null
        : createPortal(
            <button
              type="button"
              className={`${String(ember.ember)} ${String(header.primary)}`}
              title={t('web:booking.create.open')}
              aria-label={t('web:booking.create.open')}
              aria-disabled={writes.draftLocked ? 'true' : undefined}
              onClick={() => {
                composeFrom(writes.startNow, false);
              }}
            >
              <ScreenHeaderPlus className={header.primaryIcon} />
            </button>,
            headerActions,
          )}
      <LiveRegionAnnouncer message={writes.announcement} />
      <CalendarToolbar state={state} />
      <div className={styles.body}>
        <section
          ref={gridBox}
          className={styles.gridBox}
          aria-label={t('web:booking.grid.label')}
          aria-busy={gate.waiting}
        >
          {/* Past the whole grid in one press: the grid is one stop, but the column is what a
           * keyboard trainer most often came for. */}
          <button
            type="button"
            className={styles.skip}
            onClick={() => {
              // The column may be away: bring it out in this same press, then land on it.
              flushSync(() => {
                state.setPlanShown(true);
              });
              side.current?.focus();
            }}
          >
            {t('web:booking.grid.skipToDay')}
          </button>
          {prompt.shown ? (
            <GoogleCalendarPrompt
              prompt={{
                ...prompt,
                onNotNow: () => {
                  const inCard = gridBox.current
                    ?.querySelector('[data-google-prompt]')
                    ?.contains(document.activeElement);
                  flushSync(prompt.onNotNow);
                  // Its button left with the card; focus goes on to the grid rather than to the page.
                  if (inCard === true) focusGridStop();
                },
              }}
            />
          ) : null}
          {gate.waiting || content === null ? (
            <>
              <span className={hidden.srOnly}>{t('web:booking.grid.loading')}</span>
              {gate.skeleton ? <GridBars /> : null}
            </>
          ) : content.kind === 'failed' ? (
            <div className={`${String(styles.failure)} ${gate.reveal}`}>
              <EmptyState
                mark={<CalendarMarkIcon />}
                tone="neutral"
                title={t('web:clientCalendar.errorTitle')}
                hint={t('calendar.loadErrorMessage')}
              />
              <button
                type="button"
                className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}
                onClick={state.retry}
              >
                {t('calendar.retry')}
              </button>
            </div>
          ) : (
            <div className={`${String(styles.gridFill)} ${gate.reveal}`}>
              {/* Keyed with the grid, so each page mounts with the swap step; the hand-over (`.reveal`)
               * stays on the box above, which does not remount. */}
              <motion.div
                key={`${state.view}:${state.range.first}`}
                className={styles.gridSwap}
                data-grid-swap=""
                {...swap}
              >
                <Suspense fallback={<GridBars still />}>
                  <SessionGrid
                    view={state.view}
                    anchor={state.anchor}
                    range={state.range}
                    zone={state.zone}
                    today={state.today}
                    selectedDay={state.selectedDay}
                    openId={openSession?.id ?? null}
                    calendar={{
                      ...content.calendar,
                      grid: withPendingMoves(
                        content.calendar.grid,
                        writes.pendingMoves,
                        state.zone,
                      ),
                    }}
                    nowMs={state.nowMs}
                    drafts={[
                      ...(composing === null
                        ? []
                        : [{ id: null, span: composing.review.span, kind: composing.draft.kind }]),
                      ...createdNotYetRead(writes.created, content.calendar.grid, {
                        first: state.range.first,
                        answers: state.readAnswers,
                      }),
                    ]}
                    fresh={writes.fresh}
                    onFreshSeen={writes.freshSeen}
                    onSelectDay={state.selectDay}
                    onOpenSession={openSessionFrom}
                    onCompose={(start) => {
                      composeFrom(() => {
                        if ('span' in start) writes.startFromSpan(start.span);
                        else writes.startOnDay(start.day);
                      }, true);
                    }}
                    onDraftSpan={writes.setDraftSpan}
                    onMoveSession={(session, span) => writes.reschedule(session, span, 'grid')}
                  />
                </Suspense>
              </motion.div>
            </div>
          )}
          {writes.moveRefusal?.origin !== 'grid' ? null : (
            <p className={styles.moveNotice} role="alert">
              <span>{writes.moveRefusal.message}</span>
              <button
                type="button"
                className={styles.moveNoticeClose}
                aria-label={t('common.close')}
                onClick={writes.dismissMoveRefusal}
              >
                <CloseGlyph />
              </button>
            </p>
          )}
        </section>

        {columnShown ? (
          <aside
            ref={side}
            id="booking-day-plan"
            tabIndex={-1}
            className={styles.side}
            aria-label={t('web:booking.agenda.label')}
            aria-busy={gate.waiting}
          >
            {composing !== null ? (
              <div className={styles.sideFill}>
                <NewSessionPanel
                  composing={composing}
                  policy={state.policy}
                  videoCallOffered={state.callsOffered}
                  callChoice={state.callChoice}
                  onEdit={writes.editDraft}
                  onKind={writes.setDraftKind}
                  onSubmit={writes.submitDraft}
                  onRepeat={writes.openSeries}
                  onClose={closeDraft}
                />
              </div>
            ) : gate.waiting || content === null ? (
              gate.skeleton ? (
                <AgendaBars />
              ) : null
            ) : content.kind === 'failed' ? (
              // The column's own answer to the same failure: a quiet line in the reserved box rather
              // than an empty slab beside the grid's error.
              <div
                className={`${String(styles.sideFill)} ${String(styles.sideFailure)} ${gate.reveal}`}
              >
                <EmptyState
                  mark={<CalendarMarkIcon />}
                  tone="neutral"
                  title={t('web:booking.agenda.unavailable')}
                  hint={t('web:booking.agenda.unavailableHint')}
                />
              </div>
            ) : (
              // ONE box across the day's list and the open session, so moving between the two is a
              // swap inside it and never replays the hand-over.
              <div className={`${String(styles.sideFill)} ${gate.reveal}`}>
                {openSession === null ? (
                  <DayAgendaPanel
                    state={state}
                    agenda={content.agenda}
                    rangeIsEmpty={content.calendar.isEmpty}
                    onOpenSession={openSessionFrom}
                    onStepDay={(direction) => {
                      setRefocusStep(direction);
                      state.stepDay(direction);
                    }}
                    refocusStep={refocusStep}
                    onRefocused={() => {
                      setRefocusStep(null);
                    }}
                  />
                ) : (
                  <SessionDetailPanel
                    key={openSession.id}
                    session={openSession}
                    zone={state.zone}
                    nowMs={state.nowMs}
                    moveRefusal={
                      writes.moveRefusal?.origin === 'form' &&
                      writes.moveRefusal.sessionId === openSession.id
                        ? writes.moveRefusal.message
                        : null
                    }
                    onReschedule={(target) => writes.reschedule(openSession, target, 'form')}
                    onMoveEdit={writes.dismissMoveRefusal}
                    onClose={closeSession}
                  />
                )}
              </div>
            )}
          </aside>
        ) : null}
      </div>

      {writes.seriesSeed === null ? null : (
        <SeriesDialog
          seed={writes.seriesSeed}
          zone={state.zone}
          policy={state.policy}
          saving={writes.seriesSaving}
          refusal={writes.seriesRefusal}
          nowMs={state.nowMs}
          onOnce={writes.backToOnce}
          onClose={writes.closeSeries}
          onSubmit={writes.submitSeries}
        />
      )}
    </div>
  );
}
